import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { TGetCompanyOutputDto } from "@/api/services/main/company/dtos/get-company-list.dto";
import { useGetMainIndustryList, useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { findLabelByValue } from "@/utils/object";
import { Box } from "@mui/material";
import { divider } from "@/theme/colors";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import { useNavigate } from "react-router-dom";
import { COMPANY_PAGE_STORAGE_KEY } from "@/features/company/constants/common";
import { convertDateFromIsoString } from "@/features/company/utiles/datetime";
import { truncateWithEllipsis } from "@/utils/string";
import { MAX_COMPANY_NAME_LENGTH } from "@/features/company/constants/length";
import { TCompanyDisplayColumnSetting } from "@/features/company/components/company-list/company-filter-shared";

type TCompanyTableProps = {
  rows: TGetCompanyOutputDto[];
  isLoading: boolean;
  displayColumnSettings?: TCompanyDisplayColumnSetting[] | null;
};

export const CompanyTable = ({ rows, isLoading, displayColumnSettings }: TCompanyTableProps) => {
  const { mainIndustryOptions } = useGetMainIndustryList();
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const navigate = useNavigate();

  const truncateText = (value: string | null | undefined) => {
    if (!value) return EMPTY_STR.TEXT;
    return truncateWithEllipsis(value, MAX_COMPANY_NAME_LENGTH);
  };

  const columns = useMemo<MRT_ColumnDef<TGetCompanyOutputDto>[]>(
    () => [
      {
        id: "managementNumber",
        header: "企業管理番号",
        Header: <TableSorting name="managementNumber">企業管理番号</TableSorting>,
        accessorKey: "managementNumber",
        size: 116,
        Cell: ({ row }) => truncateText(row.original.managementNumber),
      },
      {
        id: "companyName",
        header: "企業名",
        accessorKey: "companyName",
        size: 292,
        Header: <TableSorting name="companyName">企業名</TableSorting>,
        Cell: ({ row }) => {
          const text = row.original.companyName || EMPTY_STR.TEXT;
          return <Box title={text}>{truncateWithEllipsis(text, MAX_COMPANY_NAME_LENGTH)}</Box>;
        },
      },
      {
        id: "companySpecificIndustryId",
        header: "分野",
        size: 172,
        Cell: ({ row }) =>
          findLabelByValue(companySpecificIndustryOptions, row.original.industryClassification?.companySpecificIndustryId1) || EMPTY_STR.TEXT,
        Header: <TableSorting name="companySpecificIndustryId1">分野</TableSorting>,
      },
      {
        id: "prefecture",
        header: "所在地",
        accessorKey: "prefecture",
        size: 116,
        Header: <TableSorting name="prefecture">所在地</TableSorting>,
        Cell: ({ row }) => truncateText(row.original.prefecture),
      },
      {
        id: "numberOfEmployees",
        header: "従業員数",
        size: 104,
        Cell: ({ row }) => truncateText(row.original.numberOfEmployees),
      },
      {
        id: "contactPersonName",
        header: "担当者",
        accessorKey: "contactPersonName",
        size: 106,
        Header: <TableSorting name="contactPersonName">担当者</TableSorting>,
        Cell: ({ row }) => truncateText(row.original.contactPersonName) || EMPTY_STR.TEXT,
      },
      {
        id: "jobCount",
        header: "求人数",
        Cell: ({ row }) => row.original.jobCount ?? 0,
        size: 74,
      },
      {
        id: "createdAt",
        header: "登録日",
        size: 106,
        Cell: ({ row }) => convertDateFromIsoString(row.original.createdAt),
        Header: <TableSorting name="createdAt">登録日</TableSorting>,
      },
    ],
    [mainIndustryOptions, companySpecificIndustryOptions],
  );

  const toEditRoute = (companyId: number) => {
    const query = new URLSearchParams(location.search);
    const currentPage = query.get("page") || "1";
    sessionStorage.setItem(COMPANY_PAGE_STORAGE_KEY, currentPage);

    const route = getDynamicRoute(AppRoutes.companyDetail, { companyId: companyId });
    navigate(route);
  };

  const { table } = useTable({ columns, rows, onClickRow: (row) => toEditRoute(row.id), renderEmptyRowsFallback: ({}) => <span></span> });

  // Apply column order and visibility via MRT table API (same pattern as JobListTable)
  useEffect(() => {
    if (!displayColumnSettings) return;

    const visibleIds = displayColumnSettings.filter((s) => s.visible).map((s) => s.id);
    table.setColumnOrder([...visibleIds]);

    const visibilityMap: Record<string, boolean> = {};
    displayColumnSettings.forEach((s) => {
      visibilityMap[s.id] = s.visible;
    });
    table.setColumnVisibility(visibilityMap);
  }, [displayColumnSettings, table]);

  const [heightTable] = useState<string>("calc(100vh - 343px)");

  return (
    <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} headRowHeight={40} bodyRowHeight={42} borderColor={divider.high} />
  );
};
